<script setup lang="ts">
import AppTable from "@/components/app-table.vue";
import type { InactiveUserDTO } from "@/models/dtos/inactive-user-dto";
import type { UserDTO } from "@/models/dtos/user-dto";
import { Role } from "@/models/enums/role";
import { UserStatus } from "@/models/enums/user-status";
import { TableColumn } from "@/models/table-column";
import { notificationsService } from "@/services/notifications-service";
import { usersService } from "@/services/users-service";
import { mdiAccountPlus, mdiCloseThick } from "@mdi/js";
import { computed, onBeforeMount, ref, toRefs } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  customerNumber?: string;
  role?: Role;
  deleteable?: boolean;
  statusInfoColumn?: boolean;
  statusActionColumn?: boolean;
  showAllCustomerUsers?: boolean;
}>();
const { customerNumber, role, deleteable } = toRefs(props);

const emit = defineEmits<{
  (e: "click:row", user: UserDTO, customerNumber?: string): void;
  (e: "delete"): void;
}>();

const loading = ref(false);
const deleteLoadingIds = ref<number[]>([]);
const activationLoadingIds = ref<number[]>([]);

const userColumns = ref([
  TableColumn.for("name", "Namn"),
  TableColumn.for("email", "E-post"),
  TableColumn.for("remove", "Ta bort", { right: true, sort: false }),
]);

if (props.statusInfoColumn) {
  userColumns.value.push(
    TableColumn.for("status", "Status", {
      renderer: (user: UserDTO) => t("userStatus." + user.status),
    }),
  );
}

if (props.statusActionColumn) {
  userColumns.value.push(
    TableColumn.for("statusAction", "", { right: true, sort: false }),
  );
}

const visibleUserColumns = computed(() =>
  userColumns.value.filter((c) => {
    return !(
      c.key === "remove" &&
      (!deleteable.value || props.statusActionColumn)
    );
  }),
);

const users = ref<(UserDTO | InactiveUserDTO)[]>([]);
const userToDelete = ref<UserDTO | undefined>();
const deleteDialogOpen = ref(false);

onBeforeMount(async () => {
  loading.value = true;

  await loadUsers();

  loading.value = false;
});

async function loadUsers() {
  const query: Record<string, unknown> = {};

  if (customerNumber?.value) {
    query.customerNumber = customerNumber.value;
  }

  if (role?.value) {
    query.role = role.value;
  }

  users.value = await usersService.getUsers(query);

  if (props.showAllCustomerUsers && customerNumber?.value && role?.value) {
    const allInactiveUsers = await usersService.getInactiveUsers(
      customerNumber.value,
    );
    const inactiveUsersWithCorrectRole = new Map();

    allInactiveUsers.forEach((user) => {
      if (user.role === role.value) {
        inactiveUsersWithCorrectRole.set(user.email, user);
      }
    });

    if (role.value === Role.USER) {
      const customerAdminUserEmails = allInactiveUsers
        .filter((user) => user.role === Role.CUSTOMER_ADMIN)
        .map((user) => user.email);

      customerAdminUserEmails.forEach((email) => {
        inactiveUsersWithCorrectRole.delete(email);
      });
    }

    if (role.value === Role.ONE_USER) {
      const customerAdminUserEmails = allInactiveUsers
        .filter((user) => user.role === Role.ONE_USER_ADMIN)
        .map((user) => user.email);

      customerAdminUserEmails.forEach((email) => {
        inactiveUsersWithCorrectRole.delete(email);
      });
    }

    users.value.push(...inactiveUsersWithCorrectRole.values());
  }
}

function onClickRow(user: UserDTO) {
  emit("click:row", user);
}

async function openDeleteDialog(_user: UserDTO) {
  userToDelete.value = _user;
  deleteDialogOpen.value = true;
}

function closeDeleteDialog() {
  deleteDialogOpen.value = false;
  userToDelete.value = undefined;
}

async function deleteUserFromCustomer() {
  if (!userToDelete.value || !customerNumber.value) return;

  deleteLoadingIds.value.push(userToDelete.value.id);

  try {
    await usersService.deleteUserFromCustomer(
      userToDelete.value.id,
      customerNumber.value,
    );
    await loadUsers();
    notificationsService.open({
      text: "Användaren har tagits bort.",
      type: "success",
    });
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid borttagning av användaren.",
      type: "error",
      error,
    });
  } finally {
    deleteLoadingIds.value = deleteLoadingIds.value.filter(
      (id) => id !== userToDelete.value?.id,
    );
    closeDeleteDialog();
  }
}

async function activateUser(user: UserDTO | InactiveUserDTO) {
  try {
    activationLoadingIds.value.push(user.id);
    await usersService.createUser({
      id: user.id,
      email: user.email,
      name: user.name,
      phone: user.phone,
      role: role?.value ?? Role.USER,
      customerNumber: customerNumber?.value ?? "",
    });
    await loadUsers();
    notificationsService.open({
      text: "Användaren har aktiverats.",
      type: "success",
    });
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid aktivering av användaren.",
      type: "error",
      error,
    });
  } finally {
    activationLoadingIds.value = activationLoadingIds.value.filter(
      (id) => id !== user.id,
    );
  }
}
</script>

<template>
  <app-table
    :columns="visibleUserColumns"
    :items="users"
    :loading="loading"
    @click:row="onClickRow"
  >
    <template v-if="deleteable" #column:remove="{ item }">
      <button
        aria-label="Ta bort användare"
        class="button is-danger is-small"
        :class="{ 'is-loading': deleteLoadingIds.includes(item.id) }"
        @click.stop="openDeleteDialog(item)"
      >
        <span class="icon"><app-icon :icon="mdiCloseThick" /></span>
      </button>
    </template>

    <template v-if="statusActionColumn" #column:statusAction="{ item: user }">
      <template v-if="user.status === UserStatus.INACTIVE">
        <button
          class="button is-success is-light is-small mr-2"
          :class="{ 'is-loading': activationLoadingIds.includes(user.id) }"
          @click.stop="activateUser(user)"
        >
          <span class="icon">
            <app-icon :icon="mdiAccountPlus" size="16" />
          </span>
          <span>Aktivera</span>
        </button>
      </template>
      <button
        aria-label="Ta bort användare"
        class="button is-danger is-small"
        :class="{ 'is-loading': deleteLoadingIds.includes(user.id) }"
        @click.stop="openDeleteDialog(user)"
      >
        <span class="icon"><app-icon :icon="mdiCloseThick" /></span>
        <span>Ta bort</span>
      </button>
    </template>
  </app-table>

  <teleport to="body">
    <div class="modal" :class="{ 'is-active': deleteDialogOpen }">
      <div class="modal-background"></div>
      <div class="modal-content box">
        <p class="title is-4">Ta bort användare</p>

        <section class="section px-0 py-2">
          <p>
            Är du säker på att du vill ta bort användare
            <strong>{{ userToDelete?.username }}</strong
            >? En borttagning går inte att återställa!
          </p>
        </section>

        <div class="buttons is-right">
          <button
            class="button is-danger"
            :class="{
              'is-loading': deleteLoadingIds.includes(userToDelete?.id ?? 0),
            }"
            @click="deleteUserFromCustomer"
          >
            Ta bort
          </button>
          <button
            class="button"
            :disabled="deleteLoadingIds.includes(userToDelete?.id ?? 0)"
            @click="closeDeleteDialog"
          >
            Avbryt
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>
